import React, { useEffect, useState } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { SupportProvider, useSupport } from "./context/SupportContext";
import MainLayout from "../Layouts/main";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import SupportAccessView from "./components/SupportAccessView";
import TicketList from "./components/TicketList";
import TicketDetail from "./components/TicketDetail";
import CreateTicketModal from "./components/CreateTicketModal";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shadcn-in-nx/ui/tabs";
import { Button } from "@shadcn-in-nx/ui/button";
import { Card } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Progress } from "@shadcn-in-nx/ui/progress";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@shadcn-in-nx/ui/dialog";
import {
    MessageSquarePlus,
    Ticket,
    Clock,
    CheckCircle2,
    Headphones,
    ArrowLeft,
    AlertCircle,
    Calendar,
    Video,
    Sparkles,
} from "lucide-react";

function SupportPageContent() {
    const { t } = useTranslation();
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState("tickets");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showNeedsPlanModal, setShowNeedsPlanModal] = useState(false);

    const {
        supportAccess,
        supportSubscription,
        tickets,
        isLoadingAccess,
        isLoadingTickets,
        getSupportAccess,
        getTickets,
        getSupportSubscription,
    } = useSupport();

    // Handle checkout success/cancel from URL params
    useEffect(() => {
        const checkoutSuccess = searchParams.get("checkout_success");
        const checkoutCanceled = searchParams.get("checkout_canceled");

        if (checkoutSuccess === "true") {
            toast.success(t("support.checkout.success"));
            setSearchParams({});
            getSupportAccess();
            getSupportSubscription();
        } else if (checkoutCanceled === "true") {
            toast.info(t("support.checkout.canceled"));
            setSearchParams({});
        }
    }, [searchParams, setSearchParams, getSupportAccess, getSupportSubscription, t]);

    // Fetch tickets when user has access
    useEffect(() => {
        if (supportAccess?.hasAccess) {
            getTickets();
            getSupportSubscription();
        }
    }, [supportAccess?.hasAccess, getTickets, getSupportSubscription]);

    // Handle ticket selection - navigate to ticket URL
    const handleSelectTicket = (id) => {
        navigate(`/support/tickets/${id}`);
    };

    // Handle back from ticket detail
    const handleBackToList = () => {
        navigate("/support");
    };

    // Handle ticket creation success
    const handleTicketCreated = (newTicketId) => {
        setShowCreateModal(false);
        if (newTicketId) {
            navigate(`/support/tickets/${newTicketId}`);
        }
    };

    // Calculate ticket stats
    const openTickets = tickets.filter(t => t.status === "OPEN" || t.status === "IN_PROGRESS").length;
    const resolvedTickets = tickets.filter(t => t.status === "RESOLVED" || t.status === "CLOSED").length;

    // Calculate hours progress
    const subscription = supportSubscription?.subscription;
    const hoursUsed = subscription ? subscription.totalHours - subscription.remainingHours : 0;
    const hoursProgress = subscription ? (hoursUsed / subscription.totalHours) * 100 : 0;

    // Handle schedule call click
    const handleScheduleCall = () => {
        if (subscription) {
            window.open(
                "https://calendar.app.google/aLYaouxqysvkrkgv7",
                "_blank",
                "noopener,noreferrer"
            );
        } else {
            setShowNeedsPlanModal(true);
        }
    };

    // If still loading access, show loading state
    if (isLoadingAccess && !supportAccess) {
        return (
            <MainLayout>
                <div className="min-h-screen p-4 md:p-8">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex items-center justify-center min-h-[400px]">
                            <div className="flex flex-col items-center gap-4">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
                                <p className="text-muted-foreground">{t("support.loading")}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    // If user doesn't have support access, show access view with plans
    if (!supportAccess?.hasAccess) {
        return (
            <MainLayout>
                <SupportAccessView />
            </MainLayout>
        );
    }

    // If viewing a specific ticket (from URL)
    if (ticketId) {
        return (
            <MainLayout>
                <div className="min-h-screen p-4 md:p-8">
                    <div className="max-w-4xl mx-auto">
                        <Button
                            variant="ghost"
                            onClick={handleBackToList}
                            className="mb-4 -ml-2"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            {t("support.tickets.backToTickets")}
                        </Button>
                        <TicketDetail ticketId={ticketId} />
                    </div>
                </div>
            </MainLayout>
        );
    }

    // Main support dashboard with tickets
    return (
        <MainLayout>
            <div className="min-h-screen p-4 md:p-8">
                <div className="max-w-6xl mx-auto space-y-6">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                                {t("support.title")}
                            </h1>
                            <p className="text-muted-foreground">
                                {t("support.subtitle")}
                            </p>
                        </div>
                        <Button
                            onClick={() => setShowCreateModal(true)}
                            className="bg-gray-900 hover:bg-gray-800 text-white"
                        >
                            <MessageSquarePlus className="w-4 h-4 mr-2" />
                            {t("support.newTicket")}
                        </Button>
                    </div>

                    {/* Hours Remaining Card - Prominent display */}
                    {subscription && (
                        <Card className="p-6 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-white">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">
                                        <Clock className="w-7 h-7 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground font-medium">
                                            {t("support.stats.hoursRemaining")}
                                        </p>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-4xl font-bold text-purple-700">
                                                {subscription.remainingHours?.toFixed(1) || 0}
                                            </span>
                                            <span className="text-lg text-muted-foreground">
                                                / {subscription.totalHours || 0} {t("support.handsOn.hoursPerMonth")}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-1 max-w-md">
                                    <div className="flex items-center justify-between text-sm mb-2">
                                        <span className="text-muted-foreground">{t("support.subscription.hoursUsed")}</span>
                                        <span className="font-medium">{hoursUsed.toFixed(1)}h used</span>
                                    </div>
                                    <Progress value={hoursProgress} className="h-3" />
                                    {subscription.remainingHours < subscription.totalHours * 0.2 && (
                                        <div className="flex items-center gap-1 mt-2 text-amber-600 text-sm">
                                            <AlertCircle className="w-4 h-4" />
                                            <span>{t("support.stats.runningLow")}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <Card className="p-6 border-2 border-border/50">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                                    <Ticket className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">{t("support.stats.openTickets")}</p>
                                    <p className="text-2xl font-bold">{openTickets}</p>
                                </div>
                            </div>
                        </Card>

                        <Card className="p-6 border-2 border-border/50">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">{t("support.stats.resolved")}</p>
                                    <p className="text-2xl font-bold">{resolvedTickets}</p>
                                </div>
                            </div>
                        </Card>

                        <Card
                            className="p-6 border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-white cursor-pointer hover:border-orange-300 transition-colors"
                            onClick={handleScheduleCall}
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                                    <Calendar className="w-6 h-6 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">{t("support.scheduleCall.title")}</p>
                                    <p className="text-lg font-semibold text-orange-700">{t("support.scheduleCall.cta")}</p>
                                </div>
                            </div>
                        </Card>

                    </div>

                    {/* Tabs */}
                    <Tabs value={activeTab} onValueChange={(value) => {
                        if (value === "plans") {
                            navigate("/billing#support");
                        } else {
                            setActiveTab(value);
                        }
                    }} className="w-full">
                        <TabsList className="grid w-full max-w-md grid-cols-2">
                            <TabsTrigger value="tickets">{t("support.tabs.tickets")}</TabsTrigger>
                            <TabsTrigger value="plans">{t("support.tabs.plans")}</TabsTrigger>
                        </TabsList>

                        <TabsContent value="tickets" className="mt-6">
                            <TicketList
                                tickets={tickets}
                                isLoading={isLoadingTickets}
                                onSelectTicket={handleSelectTicket}
                                onCreateTicket={() => setShowCreateModal(true)}
                            />
                        </TabsContent>
                    </Tabs>
                </div>
            </div>

            {/* Create Ticket Modal */}
            <CreateTicketModal
                open={showCreateModal}
                onOpenChange={setShowCreateModal}
                onSuccess={handleTicketCreated}
            />

            {/* Needs Support Plan Modal */}
            <Dialog open={showNeedsPlanModal} onOpenChange={setShowNeedsPlanModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <div className="mx-auto w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-4">
                            <Calendar className="w-8 h-8 text-orange-600" />
                        </div>
                        <DialogTitle className="text-center text-xl">
                            {t("support.scheduleCall.modalTitle")}
                        </DialogTitle>
                        <DialogDescription className="text-center">
                            {t("support.scheduleCall.modalDescription")}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-3">
                            <div className="flex items-start gap-3">
                                <Video className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="font-medium text-sm">{t("support.scheduleCall.feature1Title")}</p>
                                    <p className="text-xs text-muted-foreground">{t("support.scheduleCall.feature1Desc")}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Headphones className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="font-medium text-sm">{t("support.scheduleCall.feature2Title")}</p>
                                    <p className="text-xs text-muted-foreground">{t("support.scheduleCall.feature2Desc")}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Sparkles className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="font-medium text-sm">{t("support.scheduleCall.feature3Title")}</p>
                                    <p className="text-xs text-muted-foreground">{t("support.scheduleCall.feature3Desc")}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Button
                            onClick={() => {
                                setShowNeedsPlanModal(false);
                                navigate("/billing#support");
                            }}
                            className="w-full bg-gray-900 hover:bg-gray-800"
                        >
                            {t("support.scheduleCall.viewPlans")}
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={() => setShowNeedsPlanModal(false)}
                            className="w-full"
                        >
                            {t("common.cancel")}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </MainLayout>
    );
}

// Wrapper with context provider
export default function SupportPage() {
    return (
        <SupportProvider>
            <SupportPageContent />
        </SupportProvider>
    );
}
